import { useEffect, useMemo } from "react";
import {
  isTrackReference,
  useLocalParticipant,
  useParticipants,
  useTracks,
  VideoTrack as VideoTrackPrimitive,
  type TrackReference,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { Mic, MicOff, Video, VideoOff } from "lucide-react";
import {
  isParticipantWaiting,
  isRecorderParticipant,
  parseParticipantRole,
} from "./config";
import { useLiveKitSession } from "./LiveKitSessionContext";
import { localCameraVideoStyle } from "./cameraVideoStyle";

// CRA + TS 4.9 reject LiveKit's ReactNode return type for JSX components.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VideoTrack = VideoTrackPrimitive as any;

type LiveKitVideoTilesProps = {
  isTrainer: boolean;
  selfName: string;
  /** Narrow screens: tiles in a horizontal strip under the stage. */
  horizontal?: boolean;
  /** Hide identities already shown on the middle stage. */
  excludeIdentities?: readonly string[];
  /** Report identities shown in this rail (for selective camera subscribe). */
  onVisibleIdentitiesChange?: (identities: string[]) => void;
};

function MediaStatusIcons({
  micOn,
  camOn,
  title,
}: {
  micOn: boolean;
  camOn: boolean;
  title?: string;
}) {
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {micOn ? (
        <Mic size={14} color="#94a3b8" aria-label="Microphone on" />
      ) : (
        <MicOff size={14} color="#f87171" aria-label="Microphone off" />
      )}
      {camOn ? (
        <Video size={14} color="#94a3b8" aria-label="Camera on" />
      ) : (
        <VideoOff size={14} color="#f87171" aria-label="Camera off" />
      )}
    </span>
  );
}

/** Meet-style tile: live video when cam on + subscribed; else letter avatar. */
function ParticipantCard({
  title,
  roleLabel,
  micOn,
  camOn,
  statusTitle,
  horizontal,
  trackRef,
  hasLiveVideo,
  isLocal = false,
}: {
  title: string;
  roleLabel: string;
  micOn: boolean;
  camOn: boolean;
  statusTitle?: string;
  horizontal?: boolean;
  trackRef?: TrackReference;
  hasLiveVideo: boolean;
  isLocal?: boolean;
}) {
  const initial = (title || "?").trim().charAt(0).toUpperCase() || "?";
  const size = horizontal ? 48 : 56;
  const showLiveVideo = hasLiveVideo && Boolean(trackRef);

  return (
    <div
      style={{
        position: "relative",
        flex: horizontal ? "1 1 140px" : "0 0 auto",
        width: horizontal ? undefined : "100%",
        height: horizontal ? "100%" : 120,
        minHeight: horizontal ? 96 : 120,
        minWidth: horizontal ? 120 : 0,
        borderRadius: 12,
        background: "#0b0d12",
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {showLiveVideo && trackRef ? (
        <VideoTrack
          trackRef={trackRef}
          style={localCameraVideoStyle(isLocal, {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            background: "#0b0d12",
          })}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "radial-gradient(circle at 30% 20%, #1e293b 0%, #0b0d12 70%)",
          }}
        >
          <div
            style={{
              width: size,
              height: size,
              borderRadius: "50%",
              background: "#1e3a5f",
              color: "#93c5fd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: horizontal ? 20 : 22,
              fontWeight: 700,
            }}
          >
            {initial}
          </div>
        </div>
      )}

      <div
        style={{
          position: "absolute",
          left: 8,
          right: 8,
          bottom: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            minWidth: 0,
            padding: "4px 8px",
            borderRadius: 6,
            background: "rgba(0,0,0,0.7)",
            color: "#fff",
            fontSize: 12,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
          <span style={{ color: "#94a3b8", fontWeight: 500 }}>
            {" "}
            · {roleLabel}
          </span>
        </div>
        <div
          style={{
            flexShrink: 0,
            padding: "4px 6px",
            borderRadius: 6,
            background: "rgba(0,0,0,0.7)",
          }}
        >
          <MediaStatusIcons micOn={micOn} camOn={camOn} title={statusTitle} />
        </div>
      </div>
    </div>
  );
}

function ConnectedTiles({
  isTrainer,
  selfName,
  horizontal,
  excludeIdentities,
  onVisibleIdentitiesChange,
}: LiveKitVideoTilesProps) {
  const liveKit = useLiveKitSession();
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();
  const cameraTracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
  ]);
  const excludeSet = useMemo(
    () => new Set(excludeIdentities || []),
    [excludeIdentities],
  );

  const tiles = useMemo(() => {
    const byIdentity = new Map<string, TrackReference>();
    for (const candidate of cameraTracks) {
      if (candidate.source !== Track.Source.Camera) continue;
      if (
        !isTrackReference(candidate) &&
        byIdentity.has(candidate.participant.identity)
      ) {
        continue;
      }
      byIdentity.set(
        candidate.participant.identity,
        candidate as unknown as TrackReference,
      );
    }

    const ordered = [...participants].sort((a, b) => {
      const aLocal = a.identity === localParticipant.identity ? 0 : 1;
      const bLocal = b.identity === localParticipant.identity ? 0 : 1;
      if (aLocal !== bLocal) return aLocal - bLocal;
      const aRole = parseParticipantRole(a.metadata, a.identity);
      const bRole = parseParticipantRole(b.metadata, b.identity);
      if (aRole === "trainer" && bRole !== "trainer") return -1;
      if (bRole === "trainer" && aRole !== "trainer") return 1;
      return (a.name || a.identity).localeCompare(b.name || b.identity);
    });

    return ordered
      .filter((p) => {
        const isLocal = p.identity === localParticipant.identity;
        const role = parseParticipantRole(p.metadata, p.identity);
        if (isParticipantWaiting(p.metadata) && !isLocal) return false;
        if (isRecorderParticipant(p.identity, p.metadata)) return false;
        // Already on middle stage — do not duplicate in the right list.
        if (excludeSet.has(p.identity)) return false;
        // Trainer: every admitted participant (self + students).
        if (isTrainer) return true;
        // Student: trainer + own tile only.
        return isLocal || role === "trainer";
      })
      .map((p) => {
        const isLocal = p.identity === localParticipant.identity;
        const role = parseParticipantRole(p.metadata, p.identity);
        const roleLabel =
          role === "trainer"
            ? "Trainer"
            : role === "student"
              ? "Student"
              : "Participant";
        const track = byIdentity.get(p.identity);
        const hasLiveVideo =
          Boolean(track) &&
          isTrackReference(track) &&
          Boolean(track.publication?.track) &&
          !track.publication?.isMuted;
        const micOn = isLocal
          ? liveKit.isMicrophoneEnabled
          : Boolean(p.isMicrophoneEnabled);
        const camOn = isLocal
          ? liveKit.isCameraEnabled
          : Boolean(p.isCameraEnabled);
        return {
          identity: p.identity,
          title: isLocal ? "You" : p.name || p.identity || "Participant",
          roleLabel: isLocal
            ? isTrainer
              ? "Trainer"
              : "Student"
            : roleLabel,
          track,
          hasLiveVideo,
          micOn,
          camOn,
          isLocal,
        };
      });
  }, [
    cameraTracks,
    excludeSet,
    isTrainer,
    liveKit.isCameraEnabled,
    liveKit.isMicrophoneEnabled,
    localParticipant.identity,
    participants,
  ]);

  useEffect(() => {
    onVisibleIdentitiesChange?.(tiles.map((t) => t.identity));
  }, [tiles, onVisibleIdentitiesChange]);

  if (!liveKit.connected) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: horizontal ? "row" : "column",
          gap: 8,
          minHeight: 0,
          height: "100%",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <ParticipantCard
          title={selfName}
          roleLabel="Connecting…"
          micOn={false}
          camOn={false}
          horizontal={horizontal}
          hasLiveVideo={false}
        />
      </div>
    );
  }

  // Trainer: hide Participants rail entirely when nobody is off-stage.
  if (tiles.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: horizontal ? "row" : "column",
        gap: 8,
        overflow: horizontal ? "auto" : "hidden",
        minHeight: 0,
        height: "100%",
        width: "100%",
      }}
    >
      {!horizontal ? (
        <div
          style={{
            fontSize: 12,
            color: "#94a3b8",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          Participants ({tiles.length})
        </div>
      ) : null}
      <div
        className="meeting-tool-hide-scrollbar"
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: horizontal ? "row" : "column",
          gap: 8,
          overflowY: horizontal ? "hidden" : "auto",
          overflowX: horizontal ? "auto" : "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {tiles.map((tile) => {
          const statusTitle =
            tile.isLocal && liveKit.mediaErrorKind === "video"
              ? "Camera blocked — allow browser permission"
              : tile.isLocal && liveKit.mediaErrorKind === "audio"
                ? "Mic issue — check device / permission"
                : undefined;

          return (
            <ParticipantCard
              key={tile.identity}
              title={tile.title}
              roleLabel={tile.roleLabel}
              micOn={tile.micOn}
              camOn={tile.camOn}
              statusTitle={statusTitle}
              horizontal={horizontal}
              trackRef={tile.track}
              hasLiveVideo={tile.hasLiveVideo}
              isLocal={tile.isLocal}
            />
          );
        })}
      </div>
    </div>
  );
}

export function LiveKitVideoTiles(props: LiveKitVideoTilesProps) {
  const liveKit = useLiveKitSession();
  if (!liveKit.inRoom) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: props.horizontal ? "row" : "column",
          gap: 8,
          minHeight: 0,
          height: "100%",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <ParticipantCard
          title={props.selfName}
          roleLabel={props.isTrainer ? "Trainer" : "Student"}
          micOn={false}
          camOn={false}
          horizontal={props.horizontal}
          hasLiveVideo={false}
        />
      </div>
    );
  }
  return <ConnectedTiles {...props} />;
}
